import React from 'react';
import { Users, Award, Target, Heart } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Vision',
      description: "To be Australia's most trusted solar partner, pioneering a future where clean energy without limits is the norm, not the exception - making sustainable living accessible, affordable, and effortless for every Australian."
    },
    {
      icon: Heart,
      title: 'Our Mission',
      description: 'Empowering homes and businesses with cutting-edge solar technology and 12 years of industry expertise, delivering exceptional value that transforms energy bills into savings while building a cleaner, greener Australia for future generations.'
    },
    {
      icon: Award,
      title: 'Our Promise',
      description: 'From consultation to installation and beyond, we guarantee premium quality, transparent pricing, and unwavering support. Your success is our success - we are committed to exceeding expectations at every touchpoint.'
    },
    {
      icon: Users,
      title: 'Our Values',
      description: 'Integrity, innovation, and customer-centricity drive everything we do. As a new company built on experienced foundations, we combine fresh energy with proven expertise to create lasting relationships and sustainable solutions.'
    }
  ];

  return (
    <>
      <SEO 
        title="About Energy Spectrum | 12 Years Solar Industry Experience Sydney"
        description="Energy Spectrum: New solar company in Sydney with 12 years combined industry experience. Professional solar panel and battery installation across NSW. Learn about our team and values."
        keywords="solar company Sydney, solar installer Leppington, solar panel company NSW, Energy Spectrum Sydney, professional solar installation"
      />
      <div className="about-container">
        {/* Hero Section */}
        <section className="page-hero">
        <div className="container">
          <h1 className="display-medium">About Energy Spectrum</h1>
          <p className="body-large page-hero-description">
            A new company powered by 12 years of solar industry experience
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2 className="heading-1">Our Story</h2>
              <p className="body-medium">
                Energy Spectrum is a newly established company founded with a simple vision: to make 
                clean energy accessible to everyone. Based in Sydney and operating across New South 
                Wales, we're bringing fresh energy to the solar industry.
              </p>
              <p className="body-medium">
                While our company is new, our expertise isn't. Our team brings over 12 years of 
                combined solar industry experience, having worked on numerous residential and commercial 
                projects throughout their careers. We combine this wealth of knowledge with cutting-edge 
                technology and exceptional service to deliver solar solutions that exceed expectations.
              </p>
              <p className="body-medium">
                As we grow, we're excited about building lasting relationships with our customers and 
                expanding our services across Australia. Every installation we complete is a step toward 
                a more sustainable future, and we're committed to making your transition to solar energy 
                seamless and rewarding.
              </p>
            </div>
            <div className="story-image">
              <img 
                src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxzb2xhciUyMGVuZXJneXxlbnwwfHx8fDE3NjA3MDY3OTh8MA&ixlib=rb-4.1.0&q=85" 
                alt="Energy Spectrum solar panels and clean energy solutions Sydney NSW" 
                className="rounded-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Values</h2>
            <p className="body-large section-description">
              The principles that guide everything we do
            </p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="value-card">
                  <div className="value-icon">
                    <IconComponent size={48} />
                  </div>
                  <h3 className="heading-3">{value.title}</h3>
                  <p className="body-medium">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Expertise</h2>
            <p className="body-large section-description">
              A team of certified professionals committed to excellence
            </p>
          </div>
          <div className="expertise-content">
            <div className="expertise-grid">
              <div className="expertise-item">
                <h3 className="heading-3">Industry-Experienced Team</h3>
                <p className="body-medium">
                  Our founders and installers bring 12 years of hands-on solar industry experience, 
                  with all necessary certifications and accreditations to ensure every installation 
                  meets the highest standards.
                </p>
              </div>
              <div className="expertise-item">
                <h3 className="heading-3">Technical Excellence</h3>
                <p className="body-medium">
                  With backgrounds in electrical engineering and renewable energy gained over years 
                  in the industry, our team designs optimal solar solutions tailored to your specific 
                  needs and property.
                </p>
              </div>
              <div className="expertise-item">
                <h3 className="heading-3">Dedicated Support</h3>
                <p className="body-medium">
                  As a new company, we're building our reputation on exceptional customer service. 
                  We're always available to answer questions, provide maintenance, and ensure your 
                  system performs at its best.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default About;